import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const ContactPage = () => {
  const { t } = useTranslation();

  const testimonials = [
    {
      initials: 'AK',
      nameKey: 'reviews.review1Name',
      propertyKey: 'reviews.review1Property',
      textKey: 'reviews.review1Text',
    },
    {
      initials: 'TM',
      nameKey: 'reviews.review2Name',
      propertyKey: 'reviews.review2Property',
      textKey: 'reviews.review2Text',
    },
    {
      initials: 'GR',
      nameKey: 'reviews.review3Name',
      propertyKey: 'reviews.review3Property',
      textKey: 'reviews.review3Text',
    },
  ];

  return (
    <main className="flex-1 w-full bg-background-dark">
      {/* Hero Section */}
      <section 
        className="relative w-full h-[400px] flex items-center justify-center bg-cover bg-center pt-24" 
        style={{ 
          backgroundImage: `linear-gradient(rgba(24, 22, 17, 0.5) 0%, rgba(24, 22, 17, 0.9) 100%), url("https://images.unsplash.com/photo-1555990538-1e6c0c7a3985?w=1600&q=80")` 
        }}
      >
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10"
        >
          <h1 className="text-white text-4xl md:text-6xl font-black leading-tight tracking-[-0.033em] mb-4">
            {t('contact.heroTitle')}
          </h1>
          <p className="text-white/60 text-lg font-medium max-w-2xl mx-auto">
            {t('contact.heroSubtitle')}
          </p>
        </motion.div>
      </section>

      {/* Contact Cards & Form Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20 -mt-20 relative z-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left Column - Contact Info */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            <div className="flex flex-col gap-4">
              {/* Location Card */}
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-surface-dark border border-white/10 rounded-xl p-6 hover:border-primary/50 transition-colors group"
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-full bg-primary/10 text-primary group-hover:bg-primary group-hover:text-background-dark transition-all">
                    <span className="material-symbols-outlined">location_on</span>
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg mb-1">{t('contact.locationTitle')}</h3>
                    <p className="text-white/60 text-sm leading-relaxed whitespace-pre-line">
                      {t('contact.locationText')}
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Phone Card */}
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="bg-surface-dark border border-white/10 rounded-xl p-6 hover:border-primary/50 transition-colors group"
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-full bg-primary/10 text-primary group-hover:bg-primary group-hover:text-background-dark transition-all">
                    <span className="material-symbols-outlined">call</span>
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg mb-1">{t('contact.phoneTitle')}</h3>
                    <p className="text-white/60 text-sm leading-relaxed mb-2">{t('contact.phoneAvailable')}</p>
                    <a className="text-white hover:text-primary font-bold text-lg block" href="tel:+385911234567">{t('common.phone')}</a>
                    <a className="text-sm text-green-500 hover:text-green-400 font-medium flex items-center gap-1 mt-1" href="https://wa.me/385911234567">
                      <span className="material-symbols-outlined text-sm">chat</span> {t('contact.chatWhatsApp')}
                    </a>
                  </div>
                </div>
              </motion.div>

              {/* Email Card */}
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="bg-surface-dark border border-white/10 rounded-xl p-6 hover:border-primary/50 transition-colors group"
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-full bg-primary/10 text-primary group-hover:bg-primary group-hover:text-background-dark transition-all">
                    <span className="material-symbols-outlined">mail</span>
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg mb-1">{t('contact.emailTitle')}</h3>
                    <p className="text-white/60 text-sm leading-relaxed mb-2">{t('contact.emailResponse')}</p>
                    <a className="text-white hover:text-primary font-bold text-lg" href="mailto:info@splitapartmani.hr">{t('common.email')}</a>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Response Time */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-primary/10 border border-primary/20 rounded-xl p-6"
            >
              <div className="flex items-center gap-3 mb-2">
                <span className="material-symbols-outlined text-primary">schedule</span>
                <h3 className="text-white font-bold">{t('contact.quickResponseTitle')}</h3>
              </div>
              <p className="text-white/60 text-sm">{t('contact.quickResponseText')}</p>
            </motion.div>
          </div>

          {/* Right Column - Form */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-7"
          >
            <div className="bg-surface-dark border border-white/10 rounded-xl p-8 md:p-10 shadow-2xl">
              <div className="mb-8">
                <h2 className="text-white text-3xl font-bold mb-2">{t('contact.formTitle')}</h2>
                <p className="text-white/60">{t('contact.formSubtitle')}</p>
              </div>
              <form className="flex flex-col gap-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <label className="flex flex-col gap-2">
                    <span className="text-white text-sm font-medium">{t('contact.yourName')}</span>
                    <input 
                      className="w-full rounded-lg bg-background-dark border border-white/10 text-white placeholder-white/30 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 outline-none transition-all" 
                      placeholder="John Doe" 
                      type="text"
                    />
                  </label>
                  <label className="flex flex-col gap-2">
                    <span className="text-white text-sm font-medium">{t('contact.emailAddress')}</span>
                    <input 
                      className="w-full rounded-lg bg-background-dark border border-white/10 text-white placeholder-white/30 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 outline-none transition-all" 
                      placeholder="john@example.com" 
                      type="email"
                    />
                  </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <label className="flex flex-col gap-2">
                    <span className="text-white text-sm font-medium">{t('contact.phoneOptional')}</span>
                    <input 
                      className="w-full rounded-lg bg-background-dark border border-white/10 text-white placeholder-white/30 focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 outline-none transition-all" 
                      placeholder="+385 ..." 
                      type="tel"
                    />
                  </label>
                  <label className="flex flex-col gap-2">
                    <span className="text-white text-sm font-medium">{t('contact.helpWith')}</span>
                    <div className="relative">
                      <select className="w-full appearance-none rounded-lg bg-background-dark border border-white/10 text-white focus:border-primary focus:ring-1 focus:ring-primary h-12 px-4 outline-none transition-all pr-10">
                        <option>{t('contact.helpBooking')}</option>
                        <option>{t('contact.helpProperty')}</option>
                        <option>{t('contact.helpCheckin')}</option>
                        <option>{t('contact.helpOther')}</option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-white/50">
                        <span className="material-symbols-outlined">expand_more</span>
                      </div>
                    </div>
                  </label>
                </div>
                <label className="flex flex-col gap-2">
                  <span className="text-white text-sm font-medium">{t('contact.yourMessage')}</span>
                  <textarea 
                    className="w-full rounded-lg bg-background-dark border border-white/10 text-white placeholder-white/30 focus:border-primary focus:ring-1 focus:ring-primary p-4 outline-none transition-all resize-none" 
                    placeholder={t('contact.messagePlaceholder')}
                    rows="5"
                  ></textarea>
                </label>
                <button 
                  className="mt-2 w-full md:w-auto self-start bg-primary hover:bg-primary-hover text-background-dark font-bold py-4 px-10 rounded-lg transition-all transform active:scale-95 flex items-center justify-center gap-2" 
                  type="button"
                >
                  <span>{t('common.sendMessage')}</span>
                  <span className="material-symbols-outlined text-lg">arrow_forward</span>
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Guest Reviews Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h2 className="text-white text-3xl font-bold mb-4">{t('contact.guestReviewsTitle')}</h2>
          <p className="text-white/60">{t('contact.guestReviewsSubtitle')}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <motion.div 
              key={testimonial.initials}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-surface-dark border border-white/10 rounded-xl p-8 flex flex-col gap-6 relative group hover:border-primary/50 transition-all duration-300"
            >
              <div className="flex items-center gap-1 text-primary">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className="material-symbols-outlined text-xl">star</span>
                ))}
              </div>
              <p className="text-white/60 leading-relaxed relative z-10">"{t(testimonial.textKey)}"</p>
              <div className="mt-auto flex items-center gap-4 pt-4 border-t border-white/10">
                <div className="size-12 rounded-full bg-gradient-to-br from-white/10 to-background-dark border border-white/10 flex items-center justify-center text-primary font-bold">
                  {testimonial.initials}
                </div>
                <div>
                  <h4 className="text-white font-bold text-sm">{t(testimonial.nameKey)}</h4>
                  <p className="text-white/50 text-xs">{t(testimonial.propertyKey)}</p>
                </div>
              </div>
              <span className="material-symbols-outlined absolute top-6 right-6 text-6xl text-white/5 select-none">format_quote</span>
            </motion.div>
          ))}
        </div>
      </div>
    </main>
  );
};

export default ContactPage;
